import React, { useEffect, useState } from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { useTranslation } from 'react-i18next';
import { apiRequest, shortDate } from '../../utils';
import './Billing.css';

/* My profile ▸ Billing — the documents this person was billed with, and what was paid on them.

   /api/billing only ever returns their own: documents they are the customer on, orders they
   placed for their company, and (as its contact person) the company's. A proposal is listed as
   what it is — not an invoice yet — so nobody pays one by mistake. */

const money = (v, currency) => {
  const n = Number(v) || 0;
  return `${currency ? `${currency} ` : ''}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const KIND_LABEL = { invoice: 'documentInvoice', proposal: 'documentProposal', credit_note: 'documentCreditNote' };
const DOWNLOAD_LABEL = { invoice: 'downloadInvoice', proposal: 'downloadProposal', credit_note: 'downloadCreditNote' };
const STATUS_LABEL = {
  paid: 'billingStatusPaid',
  open: 'billingStatusOpen',
  overdue: 'billingStatusOverdue',
  credited: 'billingStatusCredited',
  proposal: 'billingStatusProposal',
};
const METHOD_LABEL = {
  online: 'paymentOnline',
  cash: 'paymentCash',
  bankTransfer: 'paymentBankTransfer',
  other: 'paymentOther',
};

const Document = ({ d, lang }) => {
  const { t } = useTranslation('common');
  const meta = [shortDate(d.date, lang), d.event_name, d.issuer].filter(Boolean).join(' · ');
  return (
    <div className={`bl-card bl-card--${d.status}`}>
      <div className="bl-card__head">
        <span className="bl-card__title">
          {t(KIND_LABEL[d.kind])}{d.number ? ` ${d.number}` : ''}
        </span>
        {STATUS_LABEL[d.status] && (
          <span className={`bl-pill bl-pill--${d.status}`}>{t(STATUS_LABEL[d.status])}</span>
        )}
      </div>
      {meta && <div className="bl-card__meta">{meta}</div>}
      {d.customer && <div className="bl-card__meta">{d.customer}</div>}
      <div className="bl-card__amounts">
        <span>{t('total')}</span>
        <strong>{money(d.total, d.currency)}</strong>
      </div>
      {(d.status === 'open' || d.status === 'overdue') && (
        <div className="bl-card__amounts bl-card__amounts--due">
          <span>
            {t('billingAmountDue')}
            {d.due_date && <span className="bl-card__due"> · {t('billingDueBy', { date: shortDate(d.due_date, lang) })}</span>}
          </span>
          <strong>{money(d.balance, d.currency)}</strong>
        </div>
      )}
      {d.pdf_pending ? (
        <div className="bl-card__pending">{t('billingPdfPending')}</div>
      ) : (
        <a
          href={`/api/billing/${d.id}/pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="bl-download"
        >
          <Icon name="file-download" className="me-1" /> {t(DOWNLOAD_LABEL[d.kind])}
        </a>
      )}
    </div>
  );
};

const Page = () => {
  const { t, i18n } = useTranslation('common');
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    apiRequest('GET', '/billing').then(setData).catch(() => setFailed(true));
  }, []);

  const documents = data?.documents || [];
  const payments = data?.payments || [];
  const lang = i18n.language;

  return (
    <Base activeNav="user">
      <Title>{t('billing')}</Title>
      <div className="bl-page">
        {failed && <p className="text-muted">{t('somethingWentWrong')}</p>}
        {!data && !failed && (
          <p className="placeholder-glow"><span className="placeholder col-8" /></p>
        )}
        {data && documents.length === 0 && <p className="text-muted">{t('billingEmpty')}</p>}

        {documents.length > 0 && (
          <>
            <h5 className="bl-section">{t('billingDocuments')}</h5>
            {documents.map((d) => <Document key={d.id} d={d} lang={lang} />)}

            <h5 className="bl-section">{t('billingPayments')}</h5>
            {payments.length === 0 && <p className="text-muted">{t('billingNoPayments')}</p>}
            {payments.length > 0 && (
              <div className="bl-payments">
                {payments.map((p) => (
                  <div key={p.id} className="bl-payment">
                    <div className="bl-payment__main">
                      <div className="bl-payment__method">
                        {p.amount < 0 ? t('paymentRefund') : t(METHOD_LABEL[p.method] || 'paymentOther')}
                      </div>
                      <div className="bl-card__meta">
                        {[shortDate(p.date, lang), p.invoice_number && t('billingForInvoice', { number: p.invoice_number })]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <strong className={p.amount < 0 ? 'bl-payment__amount--refund' : ''}>
                      {money(p.amount, p.currency)}
                    </strong>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Base>
  );
};

export default Page;
